import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AppEnvironment } from '@bazaar/shared';
import { api } from '../lib/api.js';

interface AppConfigValue {
  environment: AppEnvironment;
  nextResetAt: string | null;
}

/**
 * The server already injects `<html data-env="training">` into index.html
 * for a training build (see api/src/training/index-html.ts), so this is
 * already correct on first paint - the `/api/config` fetch below is only a
 * defensive fallback for a stale service-worker-cached `index.html` (spec
 * training-env §2).
 */
function readEnvFromDom(): AppEnvironment {
  return document.documentElement.dataset.env === 'training' ? 'training' : 'production';
}

const AppConfigContext = createContext<AppConfigValue>({
  environment: readEnvFromDom(),
  nextResetAt: null,
});

export function AppConfigProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<AppConfigValue>({
    environment: readEnvFromDom(),
    nextResetAt: null,
  });

  useEffect(() => {
    let cancelled = false;
    api
      .getConfig()
      .then((cfg) => {
        if (cancelled) return;
        document.documentElement.dataset.env = cfg.environment;
        setValue(cfg);
      })
      .catch(() => {
        // Best-effort only - the DOM-injected value above already covers the common case.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
}

export function useAppConfig(): AppConfigValue {
  return useContext(AppConfigContext);
}
